"use client";

import { useRef } from "react";
import { AtSign, Link2, Mail, QrCode } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  SectionHead,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { LAUNCH_CARDS, LINK_DOMAIN, LINK_SLUG, QR_PATH, RULE_CHIPS } from "./data";

const CARD_ICONS: Record<string, LucideIcon> = {
  Newsletter: Mail,
  "Instagram bio": AtSign,
  "Flyer QR": QrCode,
  "Partner post": Link2,
};

/**
 * Launch — the channel matrix (same URL, four doors) beside the printed
 * flyer whose QR is the link itself, plus the routing-rule chip row.
 * Sits on the neutral wash for a mid-page beat.
 */
export default function LaunchSection() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-launch-card]", { y: 22, stagger: 0.08 });

  return (
    <SectionShell
      id="launch"
      band="neutral"
      className="scroll-mt-[var(--grid-cell,72px)] md:pt-px md:pb-px"
    >
      <div ref={rootRef} className="flex flex-col">
        <SectionHead
          eyebrow="Launch"
          title="Same link, right door for every visitor."
          lead="One link goes everywhere — add ?utm_source per channel, and the routing rules still decide what each visitor lands on. No second link to create or keep in sync."
        />

        <div
          data-grid-block
          className="grid items-stretch gap-4 max-md:mt-10 lg:grid-cols-[7fr_5fr]"
        >
          {/* channel matrix */}
          <div className="grid gap-4 sm:grid-cols-2">
            {LAUNCH_CARDS.map((ch) => {
              const Icon = CARD_ICONS[ch.name];
              return (
                <div
                  key={ch.name}
                  data-launch-card
                  className="flex flex-col gap-2.5 rounded-ref border border-line bg-surface p-5"
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px]"
                      style={{
                        color: ch.color,
                        background: `color-mix(in srgb, ${ch.color} 10%, white)`,
                      }}
                    >
                      <Icon size={15} strokeWidth={1.9} />
                    </span>
                    <span className="whitespace-nowrap text-[14px] font-bold">{ch.name}</span>
                  </div>
                  <div className="font-jetbrains text-[11.5px]">
                    <span className="text-dim">{LINK_DOMAIN}</span>
                    {LINK_SLUG}
                    <span className="mt-0.5 block text-[10px] text-dim">{ch.tag}</span>
                  </div>
                  <div className="text-[12px] leading-[1.5] text-muted">{ch.lands}</div>
                </div>
              );
            })}
          </div>

          {/* the printed flyer */}
          <div
            data-launch-card
            className="flex flex-col items-center justify-center gap-3.5 rounded-ref-lg border border-line bg-surface p-6 shadow-ref"
          >
            <div className="rounded-xl border border-line-2 bg-white px-[26px] py-[22px] text-center shadow-[0_10px_30px_-14px_rgba(16,16,40,0.22)]">
              <div className="font-onest text-[20px] font-extrabold tracking-[-0.02em]">
                SPRING SALE
              </div>
              <div className="mt-0.5 text-[12px] font-semibold text-brand-600">
                −20% storewide · until Jun 30
              </div>
              <svg
                viewBox="0 0 29 29"
                width="128"
                height="128"
                aria-hidden="true"
                className="mx-auto mt-3.5 block"
              >
                <path d={QR_PATH} fill="var(--color-ink)" />
              </svg>
              <div className="mt-2.5 font-jetbrains text-[11px] text-muted">
                {LINK_DOMAIN}
                {LINK_SLUG}
              </div>
            </div>
            <p className="m-0 max-w-[300px] text-center text-[12px] leading-[1.55] text-dim [text-wrap:pretty]">
              The QR and the link are the same object — change the destination after printing and
              the flyer follows. Reprint nothing.
            </p>
          </div>
        </div>

        {/* the rules on this link */}
        <div data-grid-block className="flex flex-wrap items-center gap-2 max-md:mt-10">
          <span className="mr-1.5 font-jetbrains text-[10.5px] uppercase tracking-[0.1em] text-dim">
            Rules on this link · Premium
          </span>
          {RULE_CHIPS.map((rc) => (
            <span
              key={rc}
              className="rounded-full border border-line-2 bg-surface-2 px-3 py-[5px] font-jetbrains text-[11px]"
            >
              {rc}
            </span>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
